import {
  XStack,
  styled,
  GetProps,
  SizableStack,
  SizableText,
  SizableTextProps,
} from '@mythical/ui'
import React from 'react'

// For use in other components to match the padding of the section
export const SECTION_PADDING = {
  x: '$3.5',
  y: '$4',
  gtXs: {
    y: '$5',
    x: '$3.5',
  },
}

const SectionWrapper = styled(SizableStack, {
  name: 'Section',
  tag: 'section',
  flexDirection: 'column',
  width: '100%',
  gap: '$3.5',
  bg: '$bgPrimary',
  variants: {
    padded: {
      true: {
        px: SECTION_PADDING.x,
        py: SECTION_PADDING.y,
        $gtXs: {
          px: SECTION_PADDING.gtXs.x,
          py: SECTION_PADDING.gtXs.y,
        },
      },
    },
    paddedX: {
      true: {
        px: SECTION_PADDING.x,
        $gtXs: {
          px: SECTION_PADDING.gtXs.x,
        },
      },
    },
    spaciousBottom: {
      true: {
        pb: '$6',
        $gtXs: {
          pb: '$8',
        },
        $gtLg: {
          pb: '$10',
        },
      },
    },
  } as const,
})

export type SectionWrapperProps = GetProps<typeof SectionWrapper>

const HeadingWrapper = styled(XStack, {
  flexDirection: 'row',
  w: '100%',
  justifyContent: 'space-between',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: '$2',
  variants: {
    centered: {
      true: {
        justifyContent: 'center',
        $gtSm: {
          justifyContent: 'space-between',
        },
      },
    },
  } as const,
})

type HeaderWrapperProps = GetProps<typeof HeadingWrapper>

export interface SectionProps extends SectionWrapperProps {
  title?: string | React.ReactNode
  titleHighlight?: string | React.ReactNode
  titleProps?: SizableTextProps
  children?: React.ReactNode
  centered?: boolean
  customCta?: React.ReactNode
  headerProps?: HeaderWrapperProps
}

const Section = ({
  title,
  titleHighlight,
  centered,
  children,
  customCta,
  titleProps,
  headerProps,
  ...props
}: SectionProps) => {
  const renderHeader = title || titleHighlight || customCta
  return (
    <SectionWrapper {...props}>
      {renderHeader && (
        <HeadingWrapper centered={centered} {...headerProps}>
          {title && (
            <SizableText
              ff="$heading"
              tag="h2"
              size="$s"
              $gtSm={{ size: '$m' }}
              {...titleProps}
            >
              {title}
              {titleHighlight && (
                <SizableText
                  ff="$heading"
                  tag="span"
                  size="$s"
                  $gtSm={{ size: '$m' }}
                  color="$fgSecondary"
                  {...titleProps}
                >
                  {' '}
                  {titleHighlight}
                </SizableText>
              )}
            </SizableText>
          )}
          {customCta && customCta}
        </HeadingWrapper>
      )}

      {children}
    </SectionWrapper>
  )
}

export default Section
export { Section }
